import { useCallback, useEffect, type CSSProperties, type ReactNode } from "react";
import { dangerButtonStyle, dialogTitleStyle } from "./destructiveStyles";

interface DestructiveConfirmationDialogProps {
  title: string;
  children: ReactNode;
  confirmLabel: string;
  busyLabel: string;
  busy?: boolean;
  error?: string | null;
  blocking?: boolean;
  testId: string;
  onConfirm: () => void;
  onClose: () => void;
  /**
   * Optional second confirming action, rendered beside the primary one. Used
   * when both outcomes destroy something and neither may be the default —
   * see ux-rules.md 15e, stop-session case D.
   */
  alternateLabel?: string;
  onAlternate?: () => void;
  /**
   * Styles the primary action as neutral rather than destructive. Required
   * when an alternate action is present, so one choice does not look safer
   * than the other.
   */
  neutralConfirm?: boolean;
}

export default function DestructiveConfirmationDialog({
  title,
  children,
  confirmLabel,
  busyLabel,
  busy = false,
  error,
  blocking = false,
  testId,
  onConfirm,
  onClose,
  alternateLabel,
  onAlternate,
  neutralConfirm = false,
}: DestructiveConfirmationDialogProps) {
  const close = useCallback(() => {
    if (!busy) onClose();
  }, [busy, onClose]);

  useEffect(() => {
    if (blocking) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") close();
    };
    document.addEventListener("keydown", onKeyDown, true);
    return () => document.removeEventListener("keydown", onKeyDown, true);
  }, [blocking, close]);

  return (
    <div
      style={overlayStyle}
      role="presentation"
      onClick={blocking ? undefined : close}
      data-testid={`${testId}-overlay`}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={`${testId}-title`}
        style={dialogStyle}
        data-testid={testId}
        onClick={(event) => event.stopPropagation()}
      >
        <h2 id={`${testId}-title`} style={dialogTitleStyle}>
          {title}
        </h2>
        <div style={bodyStyle}>
          <div style={messageStyle}>{children}</div>
          <div style={buttonRowStyle}>
            {error && (
              <p role="alert" style={errorStyle}>
                {error}
              </p>
            )}
            {blocking && (
              <button
                type="button"
                disabled={busy}
                onClick={close}
                style={{ ...secondaryButtonStyle, ...(busy ? disabledButtonStyle : null) }}
              >
                Cancel
              </button>
            )}
            <button
              type="button"
              disabled={busy}
              onClick={onConfirm}
              style={{
                ...(neutralConfirm ? choiceButtonStyle : dangerButtonStyle),
                ...(busy ? disabledButtonStyle : null),
              }}
            >
              {busy ? busyLabel : confirmLabel}
            </button>
            {alternateLabel && onAlternate && (
              <button
                type="button"
                disabled={busy}
                onClick={onAlternate}
                style={{ ...choiceButtonStyle, ...(busy ? disabledButtonStyle : null) }}
              >
                {alternateLabel}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

const EXCALIDRAW_FONT =
  "Assistant, system-ui, BlinkMacSystemFont, -apple-system, Segoe UI, Roboto, Helvetica, Arial, sans-serif";

const MODAL_SHADOW = "var(--exms-shadow-modal)";

const overlayStyle: CSSProperties = {
  position: "fixed",
  inset: 0,
  zIndex: 10002,
  background: "var(--exms-overlay-bg)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: 24,
};

const dialogStyle: CSSProperties = {
  width: "100%",
  maxWidth: 560,
  boxSizing: "border-box",
  background: "var(--island-bg-color)",
  border: "1px solid var(--dialog-border-color)",
  borderRadius: 12,
  boxShadow: MODAL_SHADOW,
  fontFamily: EXCALIDRAW_FONT,
  padding: 24,
};

const bodyStyle: CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: 16,
  marginTop: 20,
};

const messageStyle: CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: 8,
  fontSize: 15,
  lineHeight: 1.5,
};

const errorStyle: CSSProperties = {
  margin: 0,
  flex: 1,
  minWidth: 0,
  alignSelf: "center",
  color: "var(--color-danger)",
  fontSize: 13,
};

const buttonRowStyle: CSSProperties = {
  display: "flex",
  justifyContent: "flex-end",
  flexWrap: "wrap",
  gap: 12,
};

const secondaryButtonStyle: CSSProperties = {
  borderRadius: 10,
  border: "1px solid var(--dialog-border-color)",
  padding: "12px 20px",
  cursor: "pointer",
  fontSize: 15,
  fontFamily: "inherit",
  fontWeight: 700,
  whiteSpace: "nowrap",
  background: "var(--island-bg-color)",
  color: "inherit",
};

const disabledButtonStyle: CSSProperties = {
  cursor: "not-allowed",
  opacity: 0.6,
};

/**
 * Equal-weight action used when two outcomes are both consequential and
 * neither may be presented as the safe default. Both choices are rendered
 * identically in the shared primary treatment, so neither reads as safer —
 * the weighting comes from them being the same, not from a separate style.
 */
const choiceButtonStyle: CSSProperties = {
  ...secondaryButtonStyle,
  border: "1px solid var(--color-primary)",
  background: "var(--color-primary)",
  color: "var(--color-on-accent)",
};
